import React, { useState } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';
import { SignedImage } from '../global/storageUrls';

function TruckActivityReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAmbulance, setSelectedAmbulance] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedSignatures, setSelectedSignatures] = useState(null);

  const ambulances = ['Mayo 21', 'Mayo 22', 'Mayo 23', 'Mayo 24', 'Mayo 25'];

  const runReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setLoading(true);
    setReportData([]);

    try {
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');

      const allRecords = [];

      // Fetch Check-In records
      if (selectedType === 'all' || selectedType === 'check-in') {
        const { data: checkInData, error: checkInError } = await supabase
          .from('daily_truck_checks')
          .select('*')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString())
          .order('created_at', { ascending: false });

        if (checkInError) throw checkInError;

        if (checkInData) {
          checkInData.forEach(record => {
            if (selectedAmbulance === 'all' || record.ambulance === selectedAmbulance) {
              allRecords.push({
                type: 'Check-In',
                ambulance: record.ambulance,
                timestamp: new Date(record.created_at),
                submittedBy: record.user_name || `${record.first_name} ${record.last_name}`,
                oncoming_crew: record.oncoming_crew,
                fuel_level: record.fuel_level,
                odometer_reading: record.odometer_reading,
                defib_test: record.defib_30j_test,
                rawData: record
              });
            }
          });
        }
      }

      // Fetch Check-Out records
      if (selectedType === 'all' || selectedType === 'check-out') {
        const { data: checkOutData, error: checkOutError } = await supabase
          .from('truck_checkout_checks')
          .select('*')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString())
          .order('created_at', { ascending: false });

        if (checkOutError) throw checkOutError;

        if (checkOutData) {
          checkOutData.forEach(record => {
            if (selectedAmbulance === 'all' || record.ambulance === selectedAmbulance) {
              allRecords.push({
                type: 'Check-Out',
                ambulance: record.ambulance,
                timestamp: new Date(record.created_at),
                submittedBy: `${record.submitted_by_first_name} ${record.submitted_by_last_name}`,
                truck_refueled: record.truck_refueled,
                interior_cleaned: record.interior_cleaned,
                exterior_cleaned: record.exterior_cleaned,
                mechanical_concerns: record.mechanical_concerns,
                mechanical_concerns_who: record.mechanical_concerns_who,
                keys_location: record.keys_location,
                keys_next_shift_crew: record.keys_next_shift_crew,
                crew_member_1_name: record.crew_member_1_name,
                crew_member_1_signature_url: record.crew_member_1_signature_url,
                crew_member_2_name: record.crew_member_2_name,
                crew_member_2_license: record.crew_member_2_license,
                crew_member_2_signature_url: record.crew_member_2_signature_url,
                rawData: record
              });
            }
          });
        }
      }

      allRecords.sort((a, b) => b.timestamp - a.timestamp);
      setReportData(allRecords);

      if (allRecords.length === 0) {
        alert('No records found for the selected criteria.');
      }
    } catch (err) {
      console.error('Error running report:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...reportData].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (key === 'timestamp') {
        aVal = a.timestamp.getTime();
        bVal = b.timestamp.getTime();
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setReportData(sorted);
  };

  const handleViewSignatures = (record) => {
    if (record.type === 'Check-Out') {
      setSelectedSignatures({
        crew1Name: record.crew_member_1_name,
        crew1Url: record.crew_member_1_signature_url,
        crew2Name: record.crew_member_2_name,
        crew2License: record.crew_member_2_license,
        crew2Url: record.crew_member_2_signature_url
      });
      setShowSignatureModal(true);
    }
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Type', 'Ambulance', 'Date/Time', 'Submitted By'];
    const csvRows = [headers.join(',')];

    reportData.forEach(record => {
      const row = [
        record.type,
        record.ambulance,
        record.timestamp.toLocaleString(),
        record.submittedBy
      ];
      csvRows.push(row.map(cell => `"${cell}"`).join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `truck_activity_${startDate}_to_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="container">
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div className="content">
        <h1 className="title">Truck Activity Report</h1>
        <p className="subtitle">View check-in and check-out activity for all ambulances</p>

        <div className="card" style={{ marginBottom: '30px' }}>
          <h3 className="section-title">Report Filters</h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div>
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div>
              <label className="form-label">Ambulance</label>
              <select
                className="form-input"
                value={selectedAmbulance}
                onChange={(e) => setSelectedAmbulance(e.target.value)}
              >
                <option value="all">All Ambulances</option>
                {ambulances.map(amb => (
                  <option key={amb} value={amb}>{amb}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Activity Type</label>
              <select
                className="form-input"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="all">All Activity</option>
                <option value="check-in">Check-In Only</option>
                <option value="check-out">Check-Out Only</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={runReport}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Run Report'}
            </button>
            {reportData.length > 0 && (
              <button
                className="btn btn-secondary"
                onClick={exportToCSV}
              >
                Export to CSV
              </button>
            )}
          </div>
        </div>

        {reportData.length > 0 && (
          <div className="card">
            <h3 className="section-title" style={{ marginBottom: '20px' }}>
              Report Results ({reportData.length} record{reportData.length !== 1 ? 's' : ''})
            </h3>

            <table style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontSize: '13px'
            }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th onClick={() => handleSort('type')} style={{
                    cursor: 'pointer',
                    padding: '12px 8px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#333',
                    borderBottom: '2px solid #dee2e6',
                    width: '90px'
                  }}>
                    Type{getSortIndicator('type')}
                  </th>
                  <th onClick={() => handleSort('ambulance')} style={{
                    cursor: 'pointer',
                    padding: '12px 8px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#333',
                    borderBottom: '2px solid #dee2e6',
                    width: '90px'
                  }}>
                    Unit{getSortIndicator('ambulance')}
                  </th>
                  <th onClick={() => handleSort('timestamp')} style={{
                    cursor: 'pointer',
                    padding: '12px 8px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#333',
                    borderBottom: '2px solid #dee2e6',
                    width: '140px'
                  }}>
                    Date/Time{getSortIndicator('timestamp')}
                  </th>
                  <th onClick={() => handleSort('submittedBy')} style={{
                    cursor: 'pointer',
                    padding: '12px 8px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#333',
                    borderBottom: '2px solid #dee2e6',
                    width: '130px'
                  }}>
                    By{getSortIndicator('submittedBy')}
                  </th>
                  <th style={{
                    padding: '12px 8px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#333',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Details
                  </th>
                  <th style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#333',
                    borderBottom: '2px solid #dee2e6',
                    width: '110px'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((record, index) => (
                  <tr key={index} style={{
                    background: index % 2 === 0 ? 'white' : '#fafbfc',
                    borderBottom: '1px solid #e9ecef'
                  }}>
                    <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: record.type === 'Check-In' ? '#d4edff' : '#ffe8d4',
                        color: record.type === 'Check-In' ? '#0066cc' : '#cc5500',
                        whiteSpace: 'nowrap'
                      }}>
                        {record.type === 'Check-In' ? 'IN' : 'OUT'}
                      </span>
                    </td>
                    <td style={{
                      padding: '10px 8px',
                      verticalAlign: 'top',
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      {record.ambulance?.replace('Mayo ', 'M')}
                    </td>
                    <td style={{
                      padding: '10px 8px',
                      verticalAlign: 'top',
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      <div>{record.timestamp.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</div>
                      <div style={{ color: '#999' }}>
                        {record.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td style={{
                      padding: '10px 8px',
                      verticalAlign: 'top',
                      color: '#333',
                      fontSize: '12px'
                    }}>
                      {record.submittedBy}
                    </td>
                    <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                      {record.type === 'Check-In' ? (
                        <div style={{ color: '#666', lineHeight: '1.5', fontSize: '12px' }}>
                          <div><strong>Crew:</strong> {record.oncoming_crew}</div>
                          <div><strong>Fuel:</strong> {record.fuel_level} | <strong>Odo:</strong> {record.odometer_reading}</div>
                        </div>
                      ) : (
                        <div style={{ color: '#666', lineHeight: '1.5', fontSize: '12px' }}>
                          <div>
                            <strong>Fuel:</strong> {record.truck_refueled} | 
                            <strong> Clean:</strong> {record.interior_cleaned}/{record.exterior_cleaned} | 
                            <strong> Keys:</strong> {record.keys_location === 'Lock box' ? 'Box' : 'Next'}
                          </div>
                          {record.mechanical_concerns === 'Yes' && (
                            <div style={{
                              marginTop: '4px',
                              padding: '4px 6px',
                              background: '#fff3cd',
                              border: '1px solid #ffc107',
                              borderRadius: '3px',
                              fontSize: '11px',
                              color: '#856404'
                            }}>
                              ⚠️ Mech: {record.mechanical_concerns_who}
                            </div>
                          )}
                          <div style={{ marginTop: '4px', fontSize: '11px', color: '#999' }}>
                            {record.crew_member_1_name}, {record.crew_member_2_name}
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 8px', verticalAlign: 'top', textAlign: 'center' }}>
                      {record.type === 'Check-Out' && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleViewSignatures(record)}
                          style={{
                            fontSize: '11px',
                            padding: '6px 10px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Signatures
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showSignatureModal && selectedSignatures && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '25px'
            }}>
              <h2 style={{ color: '#333', fontSize: '24px', margin: 0 }}>
                Crew Signatures
              </h2>
              <button
                onClick={() => setShowSignatureModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#0E3293', marginBottom: '15px', fontSize: '18px' }}>
                Crew Member 1: {selectedSignatures.crew1Name}
              </h3>
              {selectedSignatures.crew1Url ? (
                <SignedImage
                  value={selectedSignatures.crew1Url}
                  alt="Crew Member 1 Signature"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    background: 'white',
                    padding: '10px'
                  }}
                />
              ) : (
                <p style={{ color: '#666', fontStyle: 'italic' }}>No signature available</p>
              )}
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px'
            }}>
              <h3 style={{ color: '#0E3293', marginBottom: '10px', fontSize: '18px' }}>
                Crew Member 2: {selectedSignatures.crew2Name}
              </h3>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
                <strong>License:</strong> {selectedSignatures.crew2License}
              </p>
              {selectedSignatures.crew2Url ? (
                <SignedImage
                  value={selectedSignatures.crew2Url}
                  alt="Crew Member 2 Signature"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    background: 'white',
                    padding: '10px'
                  }}
                />
              ) : (
                <p style={{ color: '#666', fontStyle: 'italic' }}>No signature available</p>
              )}
            </div>

            <button
              className="btn btn-primary btn-full"
              onClick={() => setShowSignatureModal(false)}
              style={{ marginTop: '25px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <footer className="footer">
        <p className="text-small">
          NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC
        </p>
      </footer>
    </div>
  );
}

export default TruckActivityReport;
